/* ──────────────────────────────────────────────────────────────────────────────
   Faz 4 — Satıcıya İade Belgeleri (backoffice liste)  ·  /vendor-returns

   Bu ekran SAHA BELGESİNİ (ReturnHeader) listeler. Muhasebenin elle kestiği iade
   faturası (/return-invoices → InvoicesPage variant="return") AYRI ekrandır ve
   KORUNUR: mobil kaynaklı belge orada da Invoice(Type=3) projeksiyonu olarak
   görünür. İkisi aynı Id altında buluşur — biri diğerinin yerini almaz.

   ══ c360 LİSTE ═════════════════════════════════════════════════════════════════
   Liste, projenin paylaşılan motorunu (useC360List) kullanır: SUNUCU tarafı
   sayfalama + sıralama + kolon filtre hunileri. Eskiden tüm belgeler tek atımda
   (top=500) inip client'ta filtreleniyordu — 501. belge HİÇBİR filtreyle
   bulunamıyordu ve "iade hacmi düşüktür" varsayımı listeyi sessizce kırpıyordu.

   ══ KIRMIZI BAND ═══════════════════════════════════════════════════════════════
   GİB reddi olan belge RESMÎ DEĞİLDİR — ama malı ARAÇTAN ÇIKMIŞTIR (iş kuralı 9).
   Bu ikilik listenin en üstünde kırmızıyla söylenir; sessizce bir satır olarak
   listelenirse kimse müdahale etmez, mal da belge de kayıp olur.

   Band artık SAYFADAN değil, sunucudan (GET /api/returns/gib-status?status=rejected)
   beslenir: sayfalanmış bir listede "bu sayfada red yok" demek, "red yok" DEMEK
   DEĞİLDİR — uyarı bandının 2. sayfada kaybolması en kötü hata olurdu.
   ────────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser, ViewIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  API_BASE, RETURN_STATUS, DATE_PRESETS,
  RETURN_LIST_EXPAND, RETURN_SEARCH_FIELDS,
  returnColumnFilterMeta, supportsDetailFilter,
  buildVendorReturnBaseFilter, mapReturnRow,
  fetchReturnLookups, fetchProductLabels, searchProductOptions, useReturnAudit,
  subTypeLabel, subTypeTone, isScrap, gibLabel, gibTone,
  fmtDate, fmtQty, fmtMoney, unwrap, rst,
} from './returnShared';

// Kolon filtre huni etiketlerini (Plasiyer / Satıcı Cari) besleyen lookup adı.
const lookupLabel = (list, id) => list.find((x) => x.id === id)?.name || '-';

export function VendorReturnsPage() {
  const navigate = useNavigate();
  const { canAdd } = usePageActions();
  usePageMeta('Satıcıya İade', 'Saha iade belgeleri, alış faturası dağıtımı ve GİB durumu.');

  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [selected, setSelected] = useState(null);

  const [representatives, setRepresentatives] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [productLabels, setProductLabels] = useState({});

  // Tarih ön-ayarı SUNUCU filtresidir (baseFilter) — kullanıcı kolon hunileriyle VE'lenir.
  const [datePreset, setDatePreset] = useState('30');
  // ⚠ Bozuk iade denetimi (spec §10): fotoğrafsız VEYA nedeni "diğer".
  const [onlySuspicious, setOnlySuspicious] = useState(false);

  // GİB reddi sayacı + band — sunucudan (sayfadan DEĞİL).
  const [rejected, setRejected] = useState([]);

  // Ürün hunisi alt koleksiyon lambda'sı ister; uç desteklemiyorsa huni DÜŞER (bkz. returnShared).
  const [productFilterOk, setProductFilterOk] = useState(null);
  useEffect(() => { supportsDetailFilter().then(setProductFilterOk); }, []);
  const filterMeta = useMemo(() => returnColumnFilterMeta(productFilterOk), [productFilterOk]);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Denetim modu: sunucu BOZUK iadelere daraltır (şüphelinin tam üst kümesi); fotoğraf kanıtı
  // OData'da olmadığı için "şüpheli" ayıklaması sayfa üzerinde yapılır (bkz. useReturnAudit).
  const baseFilter = useMemo(
    () => buildVendorReturnBaseFilter(datePreset, onlySuspicious ? "ReturnSubType eq 'SCRAP'" : ''),
    [datePreset, onlySuspicious],
  );

  const list = useC360List({
    apiBase: API_BASE,
    baseFilter,
    expand: RETURN_LIST_EXPAND,
    columnFilterMeta: filterMeta,
    searchFields: RETURN_SEARCH_FIELDS,
    defaultSortField: 'DocumentDate',
    defaultSortDir: 'desc',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows: rawRows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  // OData satırı → ekranın okuduğu şekil (kalem / miktar / dayanılan alış faturaları).
  const pageRows = useMemo(() => rawRows.map(mapReturnRow), [rawRows]);

  // Fotoğraf kanıtı — YALNIZ görünen sayfanın bozuk satırları için (N+1 hafifletmesi).
  const { auditMap, suspicious } = useReturnAudit(pageRows);

  // Denetim modunda temiz (şüphesiz) bozuk iadeler gizlenir. Kanıt henüz inmemiş satır
  // GÖSTERİLİR: kaybolan bir satır, "yok" değil "bilmiyoruz" demektir.
  const visibleRows = useMemo(() => (
    onlySuspicious
      ? pageRows.filter((r) => !auditMap[r.id] || suspicious(r))
      : pageRows
  ), [pageRows, onlySuspicious, auditMap, suspicious]);

  // ── Lookup'lar (huni etiketleri) ──────────────────────────────────────────
  useEffect(() => {
    fetchReturnLookups().then(({ representatives: r, vendors: v }) => {
      setRepresentatives(r);
      setVendors(v);
    });
  }, []);

  // Sayfadaki ürünlerin etiketleri (sayfa başına TEK istek).
  useEffect(() => {
    const ids = pageRows.flatMap((r) => r.productIds).filter((id) => id && !productLabels[id]);
    if (ids.length === 0) return;
    fetchProductLabels(ids).then((m) => {
      if (Object.keys(m).length) setProductLabels((prev) => ({ ...prev, ...m }));
    });
  }, [pageRows, productLabels]);

  // ── GİB reddi (band + rozet) — sunucu gerçeği, sayfadan bağımsız ──────────
  const loadRejected = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/gib-status?status=rejected`, { headers: authHeaders() });
      if (!res.ok) return;
      const g = unwrap(await res.json());
      setRejected((g?.items || []).filter((r) => Number(r.status) === RETURN_STATUS.GIB_RED));
    } catch { /* band kritik değil — liste yine çalışır */ }
  }, []);

  useEffect(() => { loadRejected(); }, [loadRejected]);

  const refreshAll = useCallback(() => { fetchData(); loadRejected(); }, [fetchData, loadRejected]);

  const repName = useCallback((id) => lookupLabel(representatives, id), [representatives]);
  const vendorName = useCallback((id) => lookupLabel(vendors, id), [vendors]);
  const productNames = useCallback(
    (ids) => (ids || []).map((id) => productLabels[id]).filter(Boolean),
    [productLabels],
  );

  // ── Kolonlar (spec §10) ───────────────────────────────────────────────────
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('returnNo', {
      header: 'Belge No', meta: { filterType: 'text' },
      cell: (info) => <span className="c360-erp-chip">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('documentDate', {
      header: 'Tarih', meta: { filterType: 'date' },
      cell: (info) => fmtDate(info.getValue()),
    }),
    columnHelper.display({
      id: 'representative', header: 'Plasiyer', enableSorting: false, meta: { filterType: 'select' },
      cell: ({ row }) => repName(row.original.representativeId),
    }),
    columnHelper.display({
      id: 'vendor', header: 'Satıcı Cari', enableSorting: false, meta: { filterType: 'select' },
      cell: ({ row }) => vendorName(row.original.vendorId),
    }),
    columnHelper.accessor('returnSubType', {
      header: 'Tip', meta: { filterType: 'select' },
      cell: (info) => (
        <StatusBadge tone={subTypeTone(info.getValue())}>{subTypeLabel(info.getValue())}</StatusBadge>
      ),
    }),
    // Ürün: satırın ürünleri (huni SUNUCUDA arar — ReturnDetails/any lambda).
    columnHelper.display({
      id: 'product', header: 'Ürün', enableSorting: false, meta: { filterType: 'asyncSelect' },
      cell: ({ row }) => {
        const names = productNames(row.original.productIds);
        if (!names.length) return <span style={rst.muted}>—</span>;
        return (
          <span title={names.join(', ')}>
            {names[0]}{names.length > 1 ? ` +${names.length - 1}` : ''}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'lineCount', header: 'Kalem', enableSorting: false,
      cell: ({ row }) => <span style={rst.num}>{row.original.lineCount ?? 0}</span>,
    }),
    columnHelper.display({
      id: 'totalQuantity', header: 'Miktar', enableSorting: false,
      cell: ({ row }) => fmtQty(row.original.totalQuantity),
    }),
    columnHelper.accessor('netAmount', {
      header: 'Tutar', enableColumnFilter: false,
      cell: (info) => fmtMoney(info.getValue()),
    }),
    columnHelper.display({
      id: 'purchaseInvoices', header: 'İlişkili Alış Fat.', enableSorting: false,
      cell: ({ row }) => {
        const nos = row.original.purchaseInvoiceNos;
        if (!nos.length) return <span style={rst.muted}>—</span>;
        return (
          <span style={rst.mono} title={nos.join(', ')}>
            {nos.slice(0, 2).join(', ')}{nos.length > 2 ? ` +${nos.length - 2}` : ''}
          </span>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: 'GİB Durumu', meta: { filterType: 'select' },
      cell: (info) => {
        const v = info.getValue();
        return (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <StatusBadge tone={gibTone(v)}>{gibLabel(v)}</StatusBadge>
            {/* Reddedilen belgenin malı ZATEN ÇIKMIŞTIR — satır bunu söylemeli. */}
            {Number(v) === RETURN_STATUS.GIB_RED && <span style={{ ...rst.danger, fontSize: 11 }}>stok düştü</span>}
          </span>
        );
      },
    }),
    columnHelper.accessor('gibRetryCount', {
      header: 'Deneme', meta: { filterType: 'number' },
      cell: (info) => <span style={rst.num}>{info.getValue() ?? 0}</span>,
    }),
    columnHelper.display({
      id: 'evidence', header: 'Kanıt', enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        if (!isScrap(r.returnSubType)) return <span style={rst.muted}>—</span>;
        const a = auditMap[r.id];
        if (!a) return <span style={rst.muted}>…</span>;
        return suspicious(r)
          ? <StatusBadge tone="bad">{a.photoCount ? 'Nedeni belirsiz' : 'Fotoğrafsız'}</StatusBadge>
          : <StatusBadge tone="ok">{a.photoCount} foto</StatusBadge>;
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false, enableSorting: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view"
                  title="Görüntüle" aria-label="Görüntüle"
                  onClick={(e) => { e.stopPropagation(); navigate(`/vendor-returns/${row.original.id}`); }}>
            <ViewIcon />
          </button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [repName, vendorName, productNames, auditMap, suspicious, navigate]);

  // Huni içeriği: Plasiyer / Satıcı Cari = küçük master (select), Ürün = sunucu-arama (asyncSelect).
  const lookupOptions = useMemo(() => ({
    representative: representatives.map((r) => ({ value: r.id, label: r.name || r.textId })),
    vendor: vendors.map((v) => ({ value: v.id, label: v.name || v.textId })),
  }), [representatives, vendors]);

  const resolveFilterProps = useCallback((columnId, meta) => {
    if (meta.type === 'asyncSelect') {
      return {
        fetchOptions: searchProductOptions,
        resolveLabel: (id) => productLabels[id] || 'ürün',
      };
    }
    return { options: meta.options || lookupOptions[columnId] };
  }, [lookupOptions, productLabels]);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('DocumentDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: visibleRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  // Dışa aktarım: FİLTRELİ TÜM kayıtlar (sayfa değil) — sunucudan $expand ile iner.
  const handleExport = async () => {
    const cols = [
      { headerName: 'Belge No', valueGetter: ({ row }) => row.returnNo || '' },
      { headerName: 'Tarih', valueGetter: ({ row }) => fmtDate(row.documentDate) },
      { headerName: 'Plasiyer', valueGetter: ({ row }) => repName(row.representativeId) },
      { headerName: 'Satıcı', valueGetter: ({ row }) => vendorName(row.vendorId) },
      { headerName: 'Tip', valueGetter: ({ row }) => subTypeLabel(row.returnSubType) },
      { headerName: 'Kalem', valueGetter: ({ row }) => row.lineCount },
      { headerName: 'Miktar', valueGetter: ({ row }) => row.totalQuantity },
      { headerName: 'Tutar', valueGetter: ({ row }) => Number(row.netAmount || 0) },
      { headerName: 'İlişkili Alış Fat.', valueGetter: ({ row }) => row.purchaseInvoiceNos.join(', ') },
      { headerName: 'GİB Durumu', valueGetter: ({ row }) => gibLabel(row.status) },
      { headerName: 'Deneme', valueGetter: ({ row }) => row.gibRetryCount ?? 0 },
      { headerName: 'Ürünler', valueGetter: ({ row }) => productNames(row.productIds).join(' | ') },
    ];
    try {
      const n = await exportRows(cols, `saticiya-iade_${new Date().toISOString().slice(0, 10)}`, mapReturnRow);
      showToast(n ? 'success' : 'error', n ? `${n} kayıt dışa aktarıldı` : 'Dışa aktarılacak veri yok');
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarım başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Belge no ile ara..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && (
        <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>
      )}
      <select style={rst.filterSelect} value={datePreset}
              onChange={(e) => { setDatePreset(e.target.value); setCurrentPage(1); }}>
        {DATE_PRESETS.map((d) => <option key={d.value} value={d.value}>Tarih: {d.label}</option>)}
      </select>
      {/* ⚠ Bozuk iade denetimi — spec §10: hurda suistimale açık bir kalemdir. */}
      <span style={{ ...rst.chip, ...(onlySuspicious ? rst.chipDanger : null) }} role="button"
            title="Fotoğrafsız veya nedeni 'diğer' olan bozuk iadeler — hurda suistimale açık bir kalemdir."
            onClick={() => { setOnlySuspicious((v) => !v); setCurrentPage(1); }}>
        ⚠ Şüpheli bozuk iade
      </span>
      <button style={s.btnSecondary} onClick={refreshAll}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
        <button type="button" title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}
                style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <ExcelIcon size={16} />
        </button>
      </span>
    </>
  );

  const metaNode = (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <StatusBadge tone={rejected.length ? 'bad' : 'ok'}>{rejected.length} GİB reddi</StatusBadge>
    </span>
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Satıcıya İade"
        subtitle="Saha iade belgeleri — GİB, iade edilen malın hangi alış faturasıyla alındığını ister."
        meta={metaNode}
        // #814 "Yeni" başlığın SAĞ ÜST köşesinde durur — Fatura/İrsaliye listeleriyle aynı yer ve stil.
        // Toolbar'ın soluna konulmuştu; diğer belge ekranları referans alındı.
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={() => navigate('/vendor-returns/new')}>+ Yeni İade Belgesi</button> : null}
        toolbar={toolbarNode}
        footer={(
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={pageSize}
            rowCount={visibleRows.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      >
        {/* KIRMIZI UYARI BANDI — belge resmî değil AMA mal stoktan düşmüş durumda. */}
        {rejected.length > 0 && (
          <div style={{ padding: '14px 16px 0' }}>
            <div style={rst.band}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>⚠</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--danger, #e2445c)' }}>
                  {rejected.length} belge GİB tarafından reddedildi — belge resmî değil, mal stoktan düştü
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', marginTop: 3 }}>
                  {rejected.slice(0, 3).map((r) => r.returnNo).filter(Boolean).join(' · ')}
                  {rejected.length > 3 ? ` · +${rejected.length - 3} belge` : ''}
                  {' — '}GİB reddi stoğu geri almaz; düzeltme ayrı belgeyle yapılır.
                </div>
              </div>
              <button type="button" style={rst.smallBtn} onClick={() => navigate('/gib-monitor')}>
                GİB İzleme
              </button>
            </div>
          </div>
        )}

        {/* Denetim modu DÜRÜSTÇE açıklanır: fotoğraf sayısı OData'da yok (bkz. useReturnAudit). */}
        {onlySuspicious && (
          <div style={{ padding: '10px 16px 0', fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
            Denetim modu: sunucu <b>bozuk</b> iadelere daraltır; fotoğraf kanıtı yalnız GÖRÜNEN SAYFA
            için çözülür (backend'de foto sayısı alanı yoktur) → sayfadaki temiz kayıtlar gizlenir,
            sayaç bozuk iade toplamını gösterir.
          </div>
        )}

        <div style={{ ...rst.blk, borderTop: 'none' }}>
          <C360Table
            table={table}
            columnFilterMeta={filterMeta}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            resolveFilterProps={resolveFilterProps}
            loading={loading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={(row) => navigate(`/vendor-returns/${row.id}`)}
            emptyText="İade belgesi bulunamadı"
          />
        </div>
      </ListPageShell>

      <Toast toast={toast} />
    </div>
  );
}

export default VendorReturnsPage;
